import { AlertCircle, ChevronDown, ChevronRight, LayoutTemplate, Search, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import type { FormTemplate } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'

import { PaletteDragItem } from './PaletteDragItem'
import type { FieldTypeConfig } from './fieldTypeConfig'

type Props = {
  items: FieldTypeConfig[]
  onAddField: (type: FormFieldType) => void
  templates?: FormTemplate[]
  templatesLoading: boolean
  templatesError: boolean
  onApplyTemplate: (template: FormTemplate) => void
  /** Shown as a delete action on the templates the user may delete. */
  onDeleteTemplate?: (template: FormTemplate) => void
  canDeleteTemplate?: (template: FormTemplate) => boolean
}

const groups: Array<{ label: string; types: FormFieldType[] }> = [
  { label: 'Text', types: [FormFieldType.TEXT, FormFieldType.PARAGRAPH] },
  { label: 'Contact', types: [FormFieldType.EMAIL, FormFieldType.PHONE, FormFieldType.URL, FormFieldType.EMAIL_MESSAGE] },
  { label: 'Numbers', types: [FormFieldType.NUMBER, FormFieldType.CURRENCY, FormFieldType.PERCENT, FormFieldType.RANGE, FormFieldType.RATING] },
  { label: 'Date & time', types: [FormFieldType.DATE, FormFieldType.DATETIME, FormFieldType.TIME] },
  { label: 'Choices', types: [FormFieldType.SELECT, FormFieldType.RADIO, FormFieldType.CHECKBOX, FormFieldType.TOGGLE, FormFieldType.COLOR] },
  { label: 'Files & actions', types: [FormFieldType.FILE_UPLOAD, FormFieldType.BUTTON] },
  { label: 'Layout', types: [FormFieldType.SECTION_HEADING, FormFieldType.STATIC_TEXT, FormFieldType.DIVIDER, FormFieldType.LINE_BREAK, FormFieldType.PAGE_BREAK] },
]

export function FieldTypePalette({ items, onAddField, templates, templatesLoading, templatesError, onApplyTemplate, onDeleteTemplate, canDeleteTemplate, draggable = true, showHeader = true }: Props & { draggable?: boolean; showHeader?: boolean }) {
  const [query, setQuery] = useState('')
  const [showTemplates, setShowTemplates] = useState(false)
  const normalizedQuery = query.trim().toLowerCase()
  const filteredGroups = useMemo(() => groups.map(group => ({
    ...group,
    items: items.filter(item => group.types.includes(item.type) && (!normalizedQuery || item.label.toLowerCase().includes(normalizedQuery))),
  })).filter(group => group.items.length > 0), [items, normalizedQuery])

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showHeader && <header className="px-5 pt-4 pb-1">
        <h2 className="text-sm font-bold uppercase tracking-wide">Field types</h2>
        <p className="mt-1 text-xs text-muted-foreground">{draggable ? 'Click to add a field, or drag it to the canvas' : 'Choose a field to add it to your form'}</p>
      </header>}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-1">
        <div className="relative mb-4">
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search fields..." aria-label="Search field types" className="bg-background pl-9" />
        </div>
        <div className="space-y-5">
          {filteredGroups.map(group => <section key={group.label}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h3>
            <div className="space-y-2">{group.items.map(config => <PaletteDragItem key={config.type} cfg={config} onAdd={onAddField} draggable={draggable} />)}</div>
          </section>)}
          {filteredGroups.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No matching field types.</p>}
        </div>

        <section className="mt-6 pt-4">
          <button type="button" aria-expanded={showTemplates} onClick={() => setShowTemplates(value => !value)} className="flex w-full items-center gap-2 text-left text-sm font-semibold">
            <LayoutTemplate className="h-4 w-4 text-muted-foreground" />Templates
            {showTemplates ? <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" /> : <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />}
          </button>
          {showTemplates && <div className="mt-3 space-y-2">
            {templatesLoading && <div className="space-y-2"><Skeleton className="h-16 w-full rounded-lg" /><Skeleton className="h-16 w-full rounded-lg" /></div>}
            {templatesError && <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /><p className="text-xs text-destructive">Couldn't load templates. Check your connection and try reloading.</p></div>}
            {!templatesLoading && !templatesError && templates?.length === 0 && <p className="text-xs italic text-muted-foreground">No templates available yet.</p>}
            {!templatesLoading && !templatesError && TEMPLATE_GROUPS.map(({ scope, label }) => {
              const group = templates?.filter(template => template.scope === scope) ?? []
              return group.length > 0 && <div key={scope} className="space-y-2">
                <h4 className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</h4>
                {group.map(template => <TemplateCard key={template.id} template={template} onApply={onApplyTemplate}
                  onDelete={onDeleteTemplate && canDeleteTemplate?.(template) ? onDeleteTemplate : undefined} />)}
              </div>
            })}
          </div>}
        </section>
      </div>
    </div>
  )
}

const TEMPLATE_GROUPS = [
  { scope: 'PLATFORM', label: 'Starter templates' },
  { scope: 'WORKSPACE', label: 'Your workspace' },
] as const

function TemplateCard({ template, onApply, onDelete }: { template: FormTemplate; onApply: (template: FormTemplate) => void; onDelete?: (template: FormTemplate) => void }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0"><p className="truncate text-sm font-semibold">{template.name}</p>{template.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{template.description}</p>}</div>
        <span className="shrink-0 text-[10px] text-muted-foreground">{template.fields.length} fields</span>
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" className="h-7 flex-1 text-xs" onClick={() => onApply(template)}>Use template</Button>
        {onDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" aria-label={`Delete template ${template.name}`} onClick={() => onDelete(template)}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
    </div>
  )
}
